// How the table builder sizes a grid's columns (TableAG).
//
// Every column is as wide as its content and its header. When the columns
// fit in the grid's box, the spare width is shared out among them in
// proportion to those widths (AG Grid's fitGridWidth); when they don't, each
// keeps its width and the grid scrolls sideways inside its own box -- never
// the page. A short, structured value (a date, a status, a pill, a count, a
// domain, an email address) is never cut off; a long free-text value (a
// name, a description, a message, a URL) is shown up to TEXT_MAX_WIDTH and
// ellipsized beyond it, with the full value in a tooltip (truncatedText). A
// header is never cut off, nor is a pill.
//
// Before, every column flexed into the grid's width down to the default
// minWidth (100 px), so on a narrow box the cells and headers were squeezed
// into "heizenrad…", "Requeste…" and a status pill clipped mid-word.
//
// The widths are measured from the rendered rows (measureColumns), the way
// AG Grid's own autosize measures them, and handed to AG Grid as each
// column's minWidth with a matching flex (sizedColumnDefs). AG Grid's flex
// layout then does the rest: it follows the box as it resizes, and it can't
// squeeze a column below its minWidth, so a box too narrow for the columns
// scrolls. The measurement only ever grows within a mount (mergeMeasured):
// a longer value further down a list widens its column when it is drawn, and
// a column never narrows under the reader's eyes.

// What a column holds, for its width: a column definition's context.fit.
// FIT_CONTENT: a short, structured value; always its full width.
// FIT_TEXT (the default): free text; its full width up to TEXT_MAX_WIDTH.
export const FIT_CONTENT = 'content'
export const FIT_TEXT = 'text'

// The widest a free-text column is made for its content (px). Wider values
// ellipsize, with the full value in a tooltip. A long email address or an
// organization's name still fits.
export const TEXT_MAX_WIDTH = 320

// Room added to a measured width (px): a little air past the cell's own
// padding. Less than AG Grid's autoSizePadding (20), which also leaves room
// for a sort arrow: here a header is measured again once it is sorted (a sort
// is a model update), so it widens for its arrow then.
export const MEASURE_PADDING = 10

// The sizing modes. AUTO is the default; the others are the person's own
// choice from the grid's footer or a dragged column edge.
//   FIT_AUTO    -- sized to content as above.
//   FIT_MANUAL  -- a column edge was dragged: as FIT_AUTO, but the
//                  definitions carry no flex, so AG Grid keeps the widths the
//                  person set (a flex re-applied on the next render would undo
//                  the drag).
//   FIT_COLUMNS -- "fit to columns": every column exactly its content
//                  (AG Grid's autosize, TableAG), no flex, no cap.
//   FIT_PAGE    -- "fit to page": every column flexes into the grid's width
//                  (its own flex, else 1) down to its own minWidth; the grid
//                  never scrolls sideways (the old default).
export const FIT_AUTO = 'auto'
export const FIT_MANUAL = 'manual'
export const FIT_COLUMNS = 'columns'
export const FIT_PAGE = 'page'

// The menu column (sized by TableAG to its icons) and a pinned column (the
// narrow first column, TableAG pinFirstColumn) have rules of their own.
const isSized = c => Boolean(c) && c.colId !== 'menu' && c.field !== 'menu' && !c.pinned

// The colId AG Grid gives each definition, in order: its colId, else its
// field, a repeat suffixed _1, _2 ... (AG Grid's ColumnKeyCreator). Two
// columns over one field (the Logins page's Status and Last active) are two
// columns, each with its own width.
export const agColIds = columns => {
  const used = new Set()
  return columns.map(c => {
    if (!c) return null
    const base = c.colId !== undefined && c.colId !== null ? String(c.colId) : c.field
    for (let n = 0; ; n++) {
      const id = base ? (n ? `${base}_${n}` : base) : String(n)
      if (!used.has(id)) {
        used.add(id)
        return id
      }
    }
  })
}

// The column definitions fitted to the page, as useFitToPage fits them:
// every column flexes (its own flex, else 1) except the menu and a pinned
// column. It is also the first paint of FIT_AUTO, before anything is
// measured: the grid is drawn fitted rather than at AG Grid's 200 px default
// and then re-laid out with the headers overlapping on the way (the
// Versioning page, dev click-through 2026-10-04).
export const withFitFlex = columns => columns && columns.map(c => (isSized(c) ? { ...c, flex: c.flex || 1 } : c))

// The width a measured column needs (px): its content, a free-text column's
// content at most TEXT_MAX_WIDTH (or the column's own context.textMax) -- but
// never less than its header, the right edge of its pills, or its widest
// email address or domain -- and never less than the column's own minWidth.
// `m` is measureColumns' { width, header, pill, token } for the column.
export const neededWidth = (col, m) => {
  const fit = (col.context && col.context.fit) || FIT_TEXT
  const cap = (col.context && col.context.textMax) || TEXT_MAX_WIDTH
  const width = m.width || 0
  const content = fit === FIT_CONTENT ? width : Math.max(Math.min(width, cap), m.header || 0, m.pill || 0, m.token || 0)
  return Math.ceil(Math.max(content, col.minWidth || 0))
}

// The column definitions handed to AG Grid for a sizing mode (see above).
// `measured` maps a colId to measureColumns' result for it. A column not
// measured yet (still loading, hidden) flexes as withFitFlex has it.
export const sizedColumnDefs = (columns, measured, mode = FIT_AUTO) => {
  if (!columns) return columns
  if (mode === FIT_PAGE) return withFitFlex(columns)
  const ids = agColIds(columns)
  return columns.map((c, i) => {
    if (!isSized(c)) return c
    if (mode === FIT_COLUMNS) return { ...c, flex: null }
    const m = measured && measured[ids[i]]
    const next = m ? { ...c, minWidth: neededWidth(c, m) } : { ...c }
    if (mode === FIT_MANUAL) {
      delete next.flex
      return next
    }
    // Spare width is shared in proportion to the widths (a column's own flex
    // still weighs its share); a measured column can't be squeezed below
    // its minWidth, so when there is no spare width the grid scrolls.
    next.flex = m ? next.minWidth * (c.flex || 1) : c.flex || 1
    return next
  })
}

// Merges a new measurement into the last: each column keeps the larger of
// each width. Returns `prev` itself when nothing grew by a pixel or more, so
// a state update with it re-renders nothing.
export const mergeMeasured = (prev, next) => {
  if (!next) return prev
  let changed = false
  const out = { ...(prev || {}) }
  Object.entries(next).forEach(([id, m]) => {
    const p = out[id]
    if (!p) {
      out[id] = m
      changed = true
      return
    }
    const keys = ['width', 'header', 'pill', 'token']
    const grown = {}
    keys.forEach(k => (grown[k] = Math.max(p[k] || 0, m[k] || 0)))
    if (keys.some(k => grown[k] >= (p[k] || 0) + 1)) {
      out[id] = grown
      changed = true
    }
  })
  return changed ? out : prev
}

const PILL = '.sw-pill, .badge'

// A value that is one email address or one domain name: structured, never cut
// off however long (a URL, with its scheme and path, is free text).
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DOMAIN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i
export const isStructuredToken = text => {
  const t = (text || '').trim()
  return t.length > 0 && (EMAIL.test(t) || DOMAIN.test(t))
}

// The right edge (from the cell's left, its right padding included) of the
// furthest line of a cell that is one email address or domain on its own --
// a stacked cell's name over its email, or an email over an organization:
// the cell's text is more than the token, but the token is still never cut
// off. 0 without one.
const tokenLineRight = cell => {
  const lines = Array.from(cell.querySelectorAll('*')).filter(el => !el.children.length && isStructuredToken(el.textContent))
  if (!lines.length) return 0
  const left = cell.getBoundingClientRect().left
  const padRight = parseFloat(window.getComputedStyle(cell).paddingRight) || 0
  return Math.max(...lines.map(el => el.getBoundingClientRect().left - left + el.scrollWidth + padRight))
}

const selectorFor = colId => `[col-id="${String(colId).replace(/["\\]/g, '\\$&')}"]`

// A copy of a rendered cell or header cell, laid out at its natural width
// (AG Grid's autoWidthCalculator does the same): its fixed width and position
// dropped, inside a row of the original's classes so the grid's styles apply.
const cloneInto = (el, form) => {
  const clone = el.cloneNode(true)
  clone.style.width = ''
  clone.style.minWidth = ''
  clone.style.maxWidth = ''
  clone.style.position = 'static'
  clone.style.left = ''
  clone.removeAttribute('title')
  const parent = document.createElement('div')
  const isHeader = el.classList.contains('ag-header-cell')
  if (isHeader) {
    parent.classList.add('ag-header', 'ag-header-row')
    parent.style.position = 'static'
  } else {
    parent.classList.add('ag-row')
  }
  const row = el.closest('.ag-header-row, .ag-row')
  if (row) row.classList.forEach(k => k !== 'ag-row-position-absolute' && parent.classList.add(k))
  parent.appendChild(clone)
  form.appendChild(parent)
  return clone
}

// The natural widths of the given columns in the grid under `root`, from its
// header cells and the rows it has drawn: { [colId]: { width, header, pill,
// token } } (px, MEASURE_PADDING included) -- width the widest of the header
// and the cells, header the header alone, pill the furthest right edge of a
// pill in a cell, token the widest cell that is one email address or domain,
// or the furthest such line of a stacked cell (0 without one). Null when nothing could be measured (no grid, or no
// layout, as in jsdom).
export const measureColumns = (root, colIds) => {
  if (!root || !colIds || !colIds.length) return null
  const body = root.querySelector('.ag-center-cols-container')
  if (!body) return null

  // One copy per column, all laid out in one pass.
  const jobs = colIds.map(colId => {
    const sel = selectorFor(colId)
    const form = document.createElement('form')
    form.style.position = 'fixed'
    form.style.visibility = 'hidden'
    form.style.top = '0'
    form.style.left = '0'
    form.setAttribute('aria-hidden', 'true')
    const header = root.querySelector(`.ag-header-cell${sel}`)
    const headerClone = header ? cloneInto(header, form) : null
    const cells = Array.from(root.querySelectorAll(`.ag-row .ag-cell${sel}`)).map(cell => cloneInto(cell, form))
    return { colId, form, headerClone, cells }
  })
  jobs.forEach(j => body.appendChild(j.form))

  const out = {}
  let any = false
  try {
    jobs.forEach(({ colId, headerClone, cells }) => {
      const header = headerClone ? headerClone.offsetWidth : 0
      let width = header
      let pill = 0
      let token = 0
      cells.forEach(cell => {
        const w = Math.max(cell.offsetWidth, cell.scrollWidth)
        width = Math.max(width, w)
        if (isStructuredToken(cell.textContent)) token = Math.max(token, w)
        else token = Math.max(token, tokenLineRight(cell))
        const pills = cell.querySelectorAll(PILL)
        if (pills.length) {
          const left = cell.getBoundingClientRect().left
          const padRight = parseFloat(window.getComputedStyle(cell).paddingRight) || 0
          pills.forEach(p => {
            pill = Math.max(pill, p.getBoundingClientRect().right - left + padRight)
          })
        }
      })
      if (width > 0) any = true
      out[colId] = {
        width: Math.ceil(width) + MEASURE_PADDING,
        header: Math.ceil(header) + MEASURE_PADDING,
        pill: pill ? Math.ceil(pill) + MEASURE_PADDING : 0,
        token: token ? Math.ceil(token) + MEASURE_PADDING : 0
      }
    })
  } finally {
    jobs.forEach(j => j.form.parentNode && j.form.parentNode.removeChild(j.form))
  }
  return any ? out : null
}

// The menu column (ColumnMenu: a row's action icons, pinned on the right).
export const MENU_COL_ID = 'menu'

// Room added to the menu column past its buttons and the cell's own 8 px
// side padding (px): a few px of air, so the column is the buttons plus that
// padding. Not AG Grid's autoSizePadding (20), which is for sort arrows.
export const MENU_AUTOSIZE_PADDING = 4

const px = value => parseFloat(value) || 0

// The width (px) the menu column needs for the action cells the grid has
// drawn: the widest cell's content, its padding and borders, and `extra`.
// 0 when no drawn cell has content (no rows yet, an infinite grid's loading
// rows, or no layout, as in jsdom).
//
// Read from the live cells. The content's children are flex items at their
// natural width, so their extent is the content's full width even when the
// column is too narrow for it; the cell centres its content, so an overflow
// spills out on both sides and the cell's scrollWidth would count only half.
export const measureMenuWidth = (root, extra = 0) => {
  if (!root) return 0
  let need = 0
  root.querySelectorAll(`.ag-row .ag-cell${selectorFor(MENU_COL_ID)}`).forEach(cell => {
    const content = cell.firstElementChild
    if (!content) return
    let left = Infinity
    let right = -Infinity
    Array.from(content.children).forEach(el => {
      const box = el.getBoundingClientRect()
      if (!box.width) return
      left = Math.min(left, box.left)
      right = Math.max(right, box.left + box.width)
    })
    if (!(right > left)) return
    const style = window.getComputedStyle(cell)
    const frame = px(style.paddingLeft) + px(style.paddingRight) + px(style.borderLeftWidth) + px(style.borderRightWidth)
    need = Math.max(need, Math.ceil(right - left + frame + extra))
  })
  return need
}

// Makes the menu column as wide as its drawn cells need (measureMenuWidth)
// and at least `floor`, the widest it needed before; it never narrows it
// (AG Grid keeps it at its minWidth or more). AG Grid's autosize reads only
// the cells drawn at that moment: on an infinite grid's first paint there
// are none (the Organizations list's column stayed at its empty header's
// width, its Edit and Delete icons cut off on both sides), and after a
// search there are only loading rows. Keeping the widest need of the mount,
// the column never shrinks and grows back while rows come and go. Returns
// the new floor.
export const fitMenuColumn = (api, root, floor = 0) => {
  const col = api && typeof api.getColumn === 'function' ? api.getColumn(MENU_COL_ID) : null
  if (!col || !root) return floor
  const need = measureMenuWidth(root, MENU_AUTOSIZE_PADDING)
  const target = Math.max(floor, need)
  // Also narrows a column AG Grid's autosize left wider (it adds its own 20 px),
  // down to the floor: never below what this mount has needed.
  if (target !== col.getActualWidth()) api.setColumnWidths([{ key: MENU_COL_ID, newWidth: target }])
  return target
}

// The full text of a grid cell that is cut off (wider than its box, or
// holding an element that is), for its tooltip; null when nothing in it is
// cut off.
// The actions cell is never one of these: its visually-hidden button names
// ("edit", "duplicate", "delete") are 1 px clipped boxes, which read as "cut
// off", and their joined words were the stray "edit / duplicate / delete"
// tooltip. The buttons have their own names and tooltips.
export const truncatedText = cell => {
  if (!cell) return null
  if (cell.getAttribute && cell.getAttribute('col-id') === MENU_COL_ID) return null
  const cut = el => el.scrollWidth > el.clientWidth + 1 && window.getComputedStyle(el).overflowX !== 'visible'
  const any = cut(cell) || Array.from(cell.querySelectorAll('*')).some(el => el instanceof window.HTMLElement && cut(el))
  if (!any) return null
  const text = (cell.innerText || cell.textContent || '').replace(/\s*\n\s*/g, '\n').trim()
  return text || null
}

// Gives a cut-off cell its full text as a tooltip (the native title), and
// takes it away again once the cell is wide enough. Only a title set here is
// ever removed.
export const syncTruncatedTitle = cell => {
  if (!cell) return
  const text = truncatedText(cell)
  if (text) {
    if (cell.getAttribute('title') !== text) cell.setAttribute('title', text)
    cell.setAttribute('data-tb-title', '')
  } else if (cell.hasAttribute('data-tb-title')) {
    cell.removeAttribute('title')
    cell.removeAttribute('data-tb-title')
  }
}
